import type { SessionJourney, SessionRecord } from '@/domain/types'

export const SESSION_AUTOSAVE_INTERVAL_MS = 60_000
export const SESSION_AUTOSAVE_IDLE_TIMEOUT_MS = 1_000

export interface SessionSaveSnapshot {
  session: SessionRecord
  journey: SessionJourney | null
  capturedAt: number
}

/** One writer and one coalesced request, owned by the recording store rather than a route. */
export class SessionAutosave {
  private timer: ReturnType<typeof setInterval> | undefined
  private idle: { cancel(): void } | undefined
  private inFlight: Promise<void> | undefined
  private pending = false
  private lastSaved: SessionSaveSnapshot | undefined

  constructor(
    private readonly snapshot: (lastSaved: SessionSaveSnapshot | undefined) => SessionSaveSnapshot | null,
    private readonly save: (snapshot: SessionSaveSnapshot) => Promise<void>,
  ) {}

  start(): void {
    this.clearSchedule()
    this.lastSaved = undefined
    this.timer = setInterval(() => this.schedule(), SESSION_AUTOSAVE_INTERVAL_MS)
  }

  /** Critical lifecycle saves bypass idle scheduling; callers decide how to report errors. */
  flush(): Promise<void> {
    this.cancelIdle()
    this.pending = true
    if (!this.inFlight) {
      this.inFlight = Promise.resolve().then(() => this.drain()).finally(() => {
        this.inFlight = undefined
        // A request can arrive between the drain's last iteration and this microtask.
        if (this.pending) return this.flush()
      })
    }
    return this.inFlight
  }

  async stop(options: { discardPending?: boolean } = {}): Promise<void> {
    this.clearSchedule()
    if (options.discardPending !== false) this.pending = false
    await this.inFlight?.catch(() => undefined)
  }

  private schedule(): void {
    if (this.idle) return
    const run = () => {
      this.idle = undefined
      void this.flush().catch(() => undefined)
    }
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: SESSION_AUTOSAVE_IDLE_TIMEOUT_MS })
      this.idle = { cancel: () => window.cancelIdleCallback(id) }
    } else {
      const id = setTimeout(run, 0)
      this.idle = { cancel: () => clearTimeout(id) }
    }
  }

  private cancelIdle(): void {
    this.idle?.cancel()
    this.idle = undefined
  }

  private clearSchedule(): void {
    clearInterval(this.timer)
    this.timer = undefined
    this.cancelIdle()
  }

  private async drain(): Promise<void> {
    let failure: unknown
    let failed = false
    while (this.pending) {
      this.pending = false
      const next = this.snapshot(this.lastSaved)
      if (!next) continue
      try {
        await this.save(next)
        this.lastSaved = next
        failed = false
      } catch (error) {
        failure = error
        failed = true
      }
    }
    if (failed) throw failure
  }
}
